"use client";

import { useEffect } from "react";

/**
 * Owns smooth in-page scrolling for the /groups route.
 *
 * Two cases are handled by a single delegated click listener:
 *
 *   1. `<a class="navigator-link" data-target="#...">` — the legacy theme
 *      pattern used by the header, footer, and static CTAs. `data-target`
 *      wins over `href` so existing markup keeps working unchanged.
 *
 *   2. **Any** `<a href="#section-id">` — covers links produced from API
 *      data (hero sliders, testimonial CTAs, brand-section buttons, etc.)
 *      without forcing each component to opt in via the legacy class.
 *
 * Both paths share the same behaviour:
 *   - Smooth scroll, accounting for the fixed header.
 *   - `history.replaceState` strips the hash so users never copy or share
 *     dirty URLs like `…/groups#contact-sec`.
 *   - Modifier-clicks (cmd/ctrl/shift/alt, middle-click) and links with
 *     `target="_blank"` fall through to the browser's default behaviour.
 *
 * The component renders nothing — it only wires up listeners.
 *
 * Why React, not `main.js`:
 *   - `main.js` runs once with `strategy="afterInteractive"` and binds to
 *     stale nodes after client-side back-navigation.
 *   - `capture: true` here also lets us `stopImmediatePropagation()` the
 *     jQuery handler in `main.js` so they don't double-animate.
 */
const HEADER_OFFSET = 90;
const TOP_TARGETS = new Set<string>(["body", "#", "#top"]);

/** Resolve the scroll target for a click. Returns `null` to bail out. */
function resolveTarget(anchor: HTMLAnchorElement): string | null {
  // 1) Legacy navigator-link: data-target wins.
  const dataTarget = anchor.getAttribute("data-target");
  if (dataTarget) return dataTarget;

  // 2) Generic hash-anchor: read the href attribute (NOT `anchor.href`,
  //    which would resolve to the absolute URL and lose the literal "#").
  const rawHref = (anchor.getAttribute("href") ?? "").trim();
  if (!rawHref.startsWith("#") || rawHref.length === 1) return null;

  // Same-page-only: if the link points to a hash on a different page
  // (rare, but possible via `href="/something#sec"`), let the browser
  // handle the actual navigation.
  return rawHref;
}

export default function NavigatorScript() {
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      // Honour modifier-clicks (open-in-new-tab, save-as) and middle-clicks.
      if (
        e.defaultPrevented ||
        e.button !== 0 ||
        e.metaKey ||
        e.ctrlKey ||
        e.shiftKey ||
        e.altKey
      ) {
        return;
      }

      const anchor = (e.target as HTMLElement | null)?.closest<HTMLAnchorElement>(
        "a",
      );
      if (!anchor) return;

      // Respect explicit external-tab intent on the markup itself.
      const target = anchor.getAttribute("target");
      if (target === "_blank") return;

      const scrollTarget = resolveTarget(anchor);
      if (!scrollTarget) return;

      let scrollTop: number | null = null;
      if (TOP_TARGETS.has(scrollTarget)) {
        // "Scroll to top" only applies on the page the link points to. A Home
        // link (href="/Group-Travel-Experiences") on a sub-page must navigate — so
        // hand off to the browser and suppress the legacy main.js handler
        // (which would otherwise scroll-to-top + preventDefault and swallow it).
        const targetPath = new URL(anchor.href).pathname;
        if (targetPath !== window.location.pathname) {
          e.stopImmediatePropagation();
          return;
        }
        scrollTop = 0;
      } else if (scrollTarget.startsWith("#") && scrollTarget.length > 1) {
        let el: HTMLElement | null = null;
        try {
          el = document.querySelector<HTMLElement>(scrollTarget);
        } catch {
          /* malformed selector — bail out */
          return;
        }
        // Section isn't on this page (e.g. a /groups#section link on a
        // sub-page): let the href navigate, suppressing the legacy handler so
        // it doesn't block the navigation.
        if (!el) {
          e.stopImmediatePropagation();
          return;
        }
        scrollTop =
          el.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
      }

      if (scrollTop === null) return;

      e.preventDefault();
      // Block the jQuery handler in main.js from also animating — only one
      // smooth-scroll engine, no double-jump on the initial page load.
      e.stopImmediatePropagation();

      window.scrollTo({ top: Math.max(0, scrollTop), behavior: "smooth" });

      // Close the mobile menu if it's open — mirrors what main.js used to do.
      document
        .querySelector(".th-menu-wrapper")
        ?.classList.remove("th-body-visible");
      document
        .querySelector(".th-menu-toggle")
        ?.classList.remove("active");

      // Keep the URL clean. `replaceState` doesn't push a new history entry
      // and doesn't fire a scroll, so back-button behaviour is preserved.
      if (window.location.hash) {
        history.replaceState(
          null,
          "",
          window.location.pathname + window.location.search,
        );
      }
    }

    // `capture: true` so we run before any later-bound handlers (including
    // jQuery's delegated ones in main.js) and our `stopImmediatePropagation`
    // can suppress them on the same event.
    document.addEventListener("click", handleClick, { capture: true });
    return () =>
      document.removeEventListener("click", handleClick, { capture: true });
  }, []);

  // Arriving from another page: header/footer links on a sub-page point at
  // `/groups#section`, so the browser lands on the home page with a hash. Smooth
  // -scroll to that section with the same header offset, then strip the hash so
  // the URL stays clean (mirrors the in-page click behaviour above).
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.length < 2) return;

    let el: HTMLElement | null = null;
    try {
      el = document.querySelector<HTMLElement>(hash);
    } catch {
      return;
    }
    if (!el) return;

    const scrollTop = el.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
    window.scrollTo({ top: Math.max(0, scrollTop), behavior: "smooth" });
    history.replaceState(
      null,
      "",
      window.location.pathname + window.location.search,
    );
  }, []);

  return null;
}
